import { useMemo, type FC } from 'react'

import { StyledText } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import queries from '@/api/queries'
import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import { getPriceDetails } from '@/components/ecom-policy/policy/helpers'
import type { Policy } from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { ecomPolicyResources } from '@/resources/ecom-policy'

export type GrossSalarySchemeProductPriceProps = {
  price: number
  isSingleProductGroup: boolean
  isFromPriceTextVisible: boolean
  isTopTaxBracket: boolean
}

export const GrossSalarySchemeProductPrice: FC<GrossSalarySchemeProductPriceProps> = ({
  price,
  isSingleProductGroup,
  isFromPriceTextVisible,
  isTopTaxBracket,
}) => {
  const policyId = usePolicyId()
  const { data } = useQuery(queries.policy(policyId), () => ecomPolicyResources.get(policyId))

  const { monthlyPrice, taxSavings, realPriceAfterTax } = useMemo(
    () =>
      data
        ? getPriceDetails(
            price,
            data.coverInterval,
            isTopTaxBracket ? data.grossSalaryTopRate : data.grossSalaryBaseRate
          )
        : { monthlyPrice: 0, taxSavings: 0, realPriceAfterTax: 0 },

    [data, price, isTopTaxBracket]
  )

  if (!data) {
    return null
  }

  if (price === 0) {
    return (
      <div className="w-full whitespace-nowrap text-left text-xs sm:text-base">
        <div className="flex flex-col items-baseline">
          <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
            <Translation id="your price" />
          </StyledText>
          <StyledText
            noMargin
            size="xl"
            colorMode="black"
            fontWeight="bold"
            className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
          >
            <Translation id="Fully covered" />
          </StyledText>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center text-left text-xs sm:text-base">
      {isSingleProductGroup ? (
        <div className="flex w-full flex-col sm:gap-1">
          <div className="flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="your price" />
            </StyledText>
            <StyledText
              noMargin
              size="xl"
              colorMode="black"
              fontWeight="bold"
              className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
            >
              <FormattedNumberWithCurrency isTextBold amount={monthlyPrice} />
            </StyledText>
          </div>

          <StyledText
            size="sm"
            colorMode="gray"
            className="!mb-1 !leading-none max-sm:!text-xs md:!mb-1.5"
          >
            <Translation
              id="Over {coverInterval} {coverInterval, plural, one {month} other {months}}"
              coverInterval={data.coverInterval || 1}
            />
          </StyledText>

          <div className="mb-1 flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="Estimated savings" />:{' '}
            </StyledText>
            <StyledText
              noMargin
              size="large"
              colorMode="black"
              fontWeight="semibold"
              className="!leading-none max-md:!text-base max-sm:!text-sm"
            >
              <FormattedNumberWithCurrency amount={taxSavings} />
            </StyledText>
          </div>

          <div className="flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="Net cost" />:{' '}
            </StyledText>
            <StyledText
              noMargin
              size="large"
              colorMode="black"
              fontWeight="semibold"
              className="!leading-none max-md:!text-base max-sm:!text-sm"
            >
              <FormattedNumberWithCurrency amount={realPriceAfterTax} />
            </StyledText>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col sm:gap-1">
          <div className="flex flex-col items-baseline">
            {isFromPriceTextVisible && (
              <StyledText
                size="small"
                colorMode="gray"
                className="!leading-none max-sm:!text-xs sm:!mb-0.5"
              >
                <Translation id="your price from" />{' '}
              </StyledText>
            )}

            <StyledText
              size="xl"
              colorMode="black"
              fontWeight="bold"
              className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
            >
              <FormattedNumberWithCurrency amount={monthlyPrice} />
            </StyledText>
          </div>

          <StyledText size="sm" colorMode="gray" className="!leading-none max-sm:!text-xs">
            <Translation
              id="Over {coverInterval} {coverInterval, plural, one {month} other {months}}"
              coverInterval={data.coverInterval || 1}
            />
          </StyledText>
        </div>
      )}
    </div>
  )
}

export const GrossSalarySchemeProductPriceWizard: FC<
  GrossSalarySchemeProductPriceProps & {
    data: Pick<Policy, 'coverInterval' | 'grossSalaryTopRate' | 'grossSalaryBaseRate'>
  }
> = ({ data, price, isSingleProductGroup, isFromPriceTextVisible, isTopTaxBracket }) => {
  const { monthlyPrice, taxSavings, realPriceAfterTax } = useMemo(
    () =>
      data
        ? getPriceDetails(
            price,
            data.coverInterval,
            isTopTaxBracket ? data.grossSalaryTopRate : data.grossSalaryBaseRate
          )
        : { monthlyPrice: 0, taxSavings: 0, realPriceAfterTax: 0 },

    [data, price, isTopTaxBracket]
  )

  if (!data) {
    return null
  }

  if (price === 0) {
    return (
      <div className="w-full whitespace-nowrap text-left text-xs sm:text-base">
        <div className="flex flex-col items-baseline">
          <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
            <Translation id="your price" />
          </StyledText>
          <StyledText
            noMargin
            size="xl"
            colorMode="black"
            fontWeight="bold"
            className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
          >
            <Translation id="Fully covered" />
          </StyledText>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center text-left text-xs sm:text-base">
      {isSingleProductGroup ? (
        <div className="flex w-full flex-col sm:gap-1">
          <div className="flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="your price" />
            </StyledText>
            <StyledText
              noMargin
              size="xl"
              colorMode="black"
              fontWeight="bold"
              className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
            >
              <FormattedNumberWithCurrency isTextBold amount={monthlyPrice} />
            </StyledText>
          </div>

          <StyledText
            size="sm"
            colorMode="gray"
            className="!mb-1 !leading-none max-sm:!text-xs md:!mb-1.5"
          >
            <Translation
              id="Over {coverInterval} {coverInterval, plural, one {month} other {months}}"
              coverInterval={data.coverInterval || 1}
            />
          </StyledText>

          <div className="mb-1 flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="Estimated savings" />:{' '}
            </StyledText>
            <StyledText
              noMargin
              size="large"
              colorMode="black"
              fontWeight="semibold"
              className="!leading-none max-md:!text-base max-sm:!text-sm"
            >
              <FormattedNumberWithCurrency amount={taxSavings} />
            </StyledText>
          </div>

          <div className="flex flex-col items-baseline">
            <StyledText size="small" colorMode="gray" className="!leading-none max-sm:!text-xs">
              <Translation id="Net cost" />:{' '}
            </StyledText>
            <StyledText
              noMargin
              size="large"
              colorMode="black"
              fontWeight="semibold"
              className="!leading-none max-md:!text-base max-sm:!text-sm"
            >
              <FormattedNumberWithCurrency amount={realPriceAfterTax} />
            </StyledText>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col sm:gap-1">
          <div className="flex flex-col items-baseline">
            {isFromPriceTextVisible && (
              <StyledText
                size="small"
                colorMode="gray"
                className="!leading-none max-sm:!text-xs sm:!mb-0.5"
              >
                <Translation id="your price from" />{' '}
              </StyledText>
            )}

            <StyledText
              size="xl"
              colorMode="black"
              fontWeight="bold"
              className="max-mobile:!text-lg !leading-none max-sm:!text-xl"
            >
              <FormattedNumberWithCurrency amount={monthlyPrice} />
            </StyledText>
          </div>

          <StyledText size="sm" colorMode="gray" className="!leading-none max-sm:!text-xs">
            <Translation
              id="Over {coverInterval} {coverInterval, plural, one {month} other {months}}"
              coverInterval={data.coverInterval || 1}
            />
          </StyledText>
        </div>
      )}
    </div>
  )
}
